'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';

import {
    getActiveAutoshipSkuSet,
    getCustomerUuid,
    useAutoshipSubscriptionsQuery,
    useDecisions,
    useUserSession,
} from '@libs/common-next';
import { getAccessToken } from '@libs/core';

const PLP_AUTOSHIP_SUBSCRIPTIONS_QUERY_OPTIONS = {
    staleTime: 0,
} as const;

export function usePlpAutoshipSubscriptionLookup() {
    const isPlpAutoshipBadgeEnabled = useDecisions('plp_as_badge');
    const isPlpAutoshipCopyEnabled = useDecisions('plp_as_copy');
    const isPerSkuSubscribedIndicatorEnabled = useDecisions('x_as_sub_ind');
    const isLoggedIn = Boolean(getAccessToken());
    const { user } = useUserSession();
    const customerUuid = getCustomerUuid(user);
    const shouldUseFreshPlpSubscriptionData = isPlpAutoshipBadgeEnabled && isPerSkuSubscribedIndicatorEnabled;
    const {
        data: customerAutoshipResponse,
        isLoading,
        isFetching,
        isError,
        isSuccess,
    } = useAutoshipSubscriptionsQuery(
        shouldUseFreshPlpSubscriptionData ? PLP_AUTOSHIP_SUBSCRIPTIONS_QUERY_OPTIONS : undefined
    );

    const [isAutoshipSubscriptionsLoading, setIsAutoshipSubscriptionsLoading] = useState(true);
    const isFreshPlpSubscriptionDataPending = shouldUseFreshPlpSubscriptionData && isFetching;
    const canUseAutoshipSubscriptionData = !isLoading && !isError && !isFreshPlpSubscriptionDataPending;

    useEffect(() => {
        if (isLoggedIn && isPlpAutoshipBadgeEnabled && (!(isSuccess || isError) || isFreshPlpSubscriptionDataPending)) {
            setIsAutoshipSubscriptionsLoading(true);
        } else {
            setIsAutoshipSubscriptionsLoading(false);
        }
    }, [isLoggedIn, isPlpAutoshipBadgeEnabled, isSuccess, isError, isFreshPlpSubscriptionDataPending]);

    const customerAutoshipData = customerAutoshipResponse?.data;

    // PLP copy: "first order" (e.g. 20%) vs recurring (5%). CWE marks recently cancelled subs as
    // hasAutoship false but isFirstAutoshipEligible false — still a returning Autoship customer.
    const isExistingAutoshipSubscriber =
        canUseAutoshipSubscriptionData &&
        (Boolean(customerAutoshipData?.hasAutoship) || customerAutoshipData?.isFirstAutoshipEligible === false);

    const activeSubscriptions = customerAutoshipData?.activeSubscriptions;

    const subscribedSkuSet = useMemo(() => {
        if (
            !isPerSkuSubscribedIndicatorEnabled ||
            isError ||
            isFreshPlpSubscriptionDataPending ||
            !activeSubscriptions
        ) {
            return null;
        }
        return getActiveAutoshipSkuSet(activeSubscriptions);
    }, [isPerSkuSubscribedIndicatorEnabled, isError, isFreshPlpSubscriptionDataPending, activeSubscriptions]);

    const shouldCheckSubscription =
        isPerSkuSubscribedIndicatorEnabled && Boolean(customerUuid) && canUseAutoshipSubscriptionData;

    const isSubscribedForTileSku = useCallback(
        (tileSku: string) => shouldCheckSubscription && (subscribedSkuSet?.has(tileSku) ?? false),
        [shouldCheckSubscription, subscribedSkuSet]
    );

    const isAutoshipQuerySettled =
        !customerUuid || (!isFreshPlpSubscriptionDataPending && (Boolean(customerAutoshipResponse) || isError));

    return {
        isExistingAutoshipSubscriber,
        isPlpAutoshipBadgeEnabled,
        isPlpAutoshipCopyEnabled,
        isPerSkuSubscribedIndicatorEnabled,
        isSubscribedForTileSku,
        isAutoshipSubscriptionsLoading: isAutoshipSubscriptionsLoading || isFreshPlpSubscriptionDataPending,
        isAutoshipQuerySettled,
    };
}
